"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/utils/class-name";
import { Tooltip, TooltipPopup, TooltipProvider, TooltipTrigger } from "./tooltip";

export const buttonVariants = cva(
    "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border font-medium text-base outline-none transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] pointer-coarse:after:absolute pointer-coarse:after:size-full pointer-coarse:after:min-h-11 pointer-coarse:after:min-w-11 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-64 data-loading:select-none data-loading:text-transparent sm:text-sm [&_svg:not([class*='opacity-'])]:opacity-100 [&_svg:not([class*='size-'])]:size-4.5 sm:[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    {
        defaultVariants: {
            size: "default",
            variant: "default",
        },
        variants: {
            size: {
                default: "min-h-8 px-[calc(--spacing(3)-1px)] py-[calc(--spacing(1.5)-1px)]",
                icon: "size-9 sm:size-8",
                "icon-lg": "size-10 sm:size-9",
                "icon-sm": "size-8 sm:size-7",
                "icon-xl":
                    "size-11 sm:size-10 [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-4.5",
                "icon-xxl":
                    "size-12 sm:size-11 [&_svg:not([class*='size-'])]:size-6 sm:[&_svg:not([class*='size-'])]:size-5",
                "icon-xs":
                    "size-7 rounded-md before:rounded-[calc(var(--radius-md)-1px)] sm:size-6 not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-4 sm:not-in-data-[slot=input-group]:[&_svg:not([class*='size-'])]:size-3.5",
                lg: "h-10 px-[calc(--spacing(3.5)-1px)] sm:h-9",
                /*
                 * A card's action buttons (Save, Add layer) at its pill rows' text and icon size (NavRowContent's
                 * text-xl label and size-6 icon), so they don't sit thin beside them; the height follows.
                 */
                pill: "min-h-8 px-4 py-2 text-xl sm:text-xl [&_svg:not([class*='size-'])]:size-6 sm:[&_svg:not([class*='size-'])]:size-6",
                sm: "h-8 gap-1.5 px-[calc(--spacing(2.5)-1px)] sm:h-7",
                xl: "h-11 px-[calc(--spacing(4)-1px)] text-lg sm:h-10 sm:text-base [&_svg:not([class*='size-'])]:size-5 sm:[&_svg:not([class*='size-'])]:size-4.5",
                xs: "h-7 gap-1 rounded-md px-[calc(--spacing(2)-1px)] text-sm before:rounded-[calc(var(--radius-md)-1px)] sm:h-6 sm:text-xs [&_svg:not([class*='size-'])]:size-4 sm:[&_svg:not([class*='size-'])]:size-3.5",
            },
            variant: {
                default:
                    "not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-primary bg-primary text-primary-foreground shadow-primary/24 shadow-xs hover:bg-primary/90 data-pressed:bg-primary/90 *:data-[slot=button-loading-indicator]:text-primary-foreground [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none",
                destructive:
                    "not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-destructive bg-destructive text-white shadow-destructive/24 shadow-xs hover:bg-destructive/90 data-pressed:bg-destructive/90 *:data-[slot=button-loading-indicator]:text-white [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none",
                "destructive-outline":
                    "border-input bg-popover not-dark:bg-clip-padding text-destructive shadow-xs/5 not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/4%)] hover:border-destructive/32 hover:bg-destructive/4 data-pressed:border-destructive/32 data-pressed:bg-destructive/4 *:data-[slot=button-loading-indicator]:text-destructive dark:bg-input/32 dark:not-disabled:before:shadow-[0_-1px_--theme(--color-white/2%)] dark:not-disabled:not-active:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/6%)] [:disabled,:active,[data-pressed]]:shadow-none",
                ghost: "border-transparent text-foreground hover:bg-accent data-pressed:bg-accent *:data-[slot=button-loading-indicator]:text-foreground",
                link: "border-transparent text-foreground underline-offset-4 hover:underline data-pressed:underline *:data-[slot=button-loading-indicator]:text-foreground",
                outline:
                    "border-input bg-popover not-dark:bg-clip-padding text-foreground shadow-xs/5 not-disabled:not-active:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/4%)] hover:bg-accent/50 data-pressed:bg-accent/50 *:data-[slot=button-loading-indicator]:text-foreground dark:bg-input/32 dark:data-pressed:bg-input/64 dark:hover:bg-input/64 dark:not-disabled:before:shadow-[0_-1px_--theme(--color-white/2%)] dark:not-disabled:not-active:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/6%)] [:disabled,:active,[data-pressed]]:shadow-none",
                secondary:
                    "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/90 data-pressed:bg-secondary/90 *:data-[slot=button-loading-indicator]:text-secondary-foreground [:active,[data-pressed]]:bg-secondary/80",
                info: "not-disabled:inset-shadow-[0_1px_--theme(--color-white/16%)] border-info bg-info text-white shadow-info/24 shadow-xs hover:bg-info/90 data-pressed:bg-info/90 *:data-[slot=button-loading-indicator]:text-white [:active,[data-pressed]]:inset-shadow-[0_1px_--theme(--color-black/8%)] [:disabled,:active,[data-pressed]]:shadow-none",
            },
        },
    },
);

export interface ButtonProps extends useRender.ComponentProps<"button"> {
    variant?: VariantProps<typeof buttonVariants>["variant"];
    size?: VariantProps<typeof buttonVariants>["size"];
    loading?: boolean;
    tooltipTriggerClassName?: string;
    tooltip?: string;
    tooltipClassName?: string;
    tooltipDelay?: number;
    tooltipDisabled?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps & React.RefAttributes<HTMLButtonElement>>(
    (
        {
            className,
            variant,
            size,
            render,
            children,
            loading = false,
            disabled: disabledProp,
            tooltipTriggerClassName,
            tooltip,
            tooltipClassName,
            tooltipDelay,
            tooltipDisabled,
            ...props
        }: ButtonProps,
        ref,
    ) => {
        const isDisabled: boolean = Boolean(loading || disabledProp);
        const typeValue: React.ButtonHTMLAttributes<HTMLButtonElement>["type"] = render ? undefined : "button";

        const defaultProps = {
            children: (
                <>
                    {children}
                    {loading && (
                        <Spinner className={"pointer-events-none absolute"} data-slot={"button-loading-indicator"} />
                    )}
                </>
            ),
            className: cn(buttonVariants({ className, size, variant })),
            "aria-disabled": loading || undefined,
            "data-loading": loading ? "" : undefined,
            "data-slot": "button",
            disabled: isDisabled,
            type: typeValue,
        };

        const buttonContent = useRender({
            defaultTagName: "button",
            props: mergeProps<"button">(defaultProps, props),
            render,
            ref,
        });

        return tooltip ? (
            <TooltipProvider delay={tooltipDelay}>
                <Tooltip disabled={tooltipDisabled}>
                    <TooltipTrigger render={buttonContent} className={cn(tooltipTriggerClassName)} />
                    <TooltipPopup className={cn(tooltipClassName)}>{tooltip}</TooltipPopup>
                </Tooltip>
            </TooltipProvider>
        ) : (
            buttonContent
        );
    },
);
